import React, { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";

const KEY = "ti_favorites";

function readAll() {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
}

function writeAll(list) { localStorage.setItem(KEY, JSON.stringify(list)); window.dispatchEvent(new CustomEvent("favs-updated")); }

export function useFavorites() {
  const [favs, setFavs] = useState(readAll());
  useEffect(() => {
    const sync = () => setFavs(readAll());
    window.addEventListener("favs-updated", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("favs-updated", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return favs;
}

export function isFavorite(type, id) {
  return readAll().some((f) => f.product_type === type && f.product_id === id);
}

export function toggleFavorite(type, id, meta = {}) {
  const current = readAll();
  const exists = current.find((f) => f.product_type === type && f.product_id === id);
  const next = exists
    ? current.filter((f) => !(f.product_type === type && f.product_id === id))
    : [...current, { product_type: type, product_id: id, ...meta, added_at: new Date().toISOString() }];
  writeAll(next);
  return !exists;
}

export default function FavoriteButton({ productType, productId, meta, className = "", variant = "circle" }) {
  const [fav, setFav] = useState(() => isFavorite(productType, productId));
  useEffect(() => {
    const sync = () => setFav(isFavorite(productType, productId));
    window.addEventListener("favs-updated", sync);
    return () => window.removeEventListener("favs-updated", sync);
  }, [productType, productId]);

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = toggleFavorite(productType, productId, meta);
    setFav(added);
    toast.success(added ? "Aggiunto ai preferiti" : "Rimosso dai preferiti");
  };

  if (variant === "icon") {
    return (
      <button
        data-testid={`fav-btn-${productId}`}
        onClick={onClick}
        aria-label={fav ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
        className={`transition-transform hover:scale-110 ${className}`}
      >
        <Heart className={`h-5 w-5 ${fav ? "fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "text-foreground/70"}`} />
      </button>
    );
  }

  return (
    <button
      data-testid={`fav-btn-${productId}`}
      onClick={onClick}
      aria-label={fav ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
      className={`inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/90 hover:bg-white shadow-sm border border-border transition-all hover:scale-105 ${className}`}
    >
      <Heart className={`h-4 w-4 ${fav ? "fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "text-foreground/70"}`} />
    </button>
  );
}
